/**
 * Integrations Panel - Connected Services Management
 * Clinical Intelligence System - Premium medical instrument aesthetic
 */

import { FC, useState, useEffect } from 'react'
import { Button } from '../ui/button'
import {
  Plug2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
  Unplug,
  RefreshCw,
  Sparkles,
  Lock,
  BookOpen,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { OpenEvidenceConnectDialog } from './OpenEvidenceConnectDialog'
import api from '../../services/api'
import { isFeatureEnabled } from '../../config/features'

interface IntegrationStatus {
  public_id: string
  integration_type: string
  status: 'active' | 'expired' | 'revoked' | 'error'
  connected_at: string
  expires_at: string
  last_used_at: string | null
  external_account_hint: string | null
  failure_count: number
  last_error: string | null
}

interface IntegrationsResponse {
  integrations: IntegrationStatus[]
}

export const IntegrationsPanel: FC = () => {
  const [integrations, setIntegrations] = useState<IntegrationStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [connectDialogOpen, setConnectDialogOpen] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)

  // Fetch integration status
  const fetchIntegrations = async () => {
    try {
      setLoading(true)
      const response = await api.get<IntegrationsResponse>('/api/v1/integrations/status')
      setIntegrations(response.data.integrations || [])
    } catch (error) {
      console.error('Failed to fetch integrations:', error)
      setIntegrations([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchIntegrations()
  }, [])

  // Find Open Evidence integration
  const oeIntegration = integrations.find(i => i.integration_type === 'open_evidence')
  const isConnected = oeIntegration?.status === 'active'
  const isExpired = oeIntegration?.status === 'expired'

  // Handle disconnect
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Open Evidence account?')) {
      return
    }

    try {
      setDisconnecting(true)
      await api.delete('/api/v1/integrations/open-evidence')
      await fetchIntegrations()
    } catch (error) {
      console.error('Failed to disconnect:', error)
    } finally {
      setDisconnecting(false)
    }
  }

  // Handle successful connection
  const handleConnectSuccess = () => {
    setConnectDialogOpen(false)
    fetchIntegrations()
  }

  // Format relative time
  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays < 0) return 'Expired'
    if (diffDays === 0) return 'Expires today'
    if (diffDays === 1) return 'Expires tomorrow'
    return `Expires in ${diffDays} days`
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-clinical-cyan/20 to-clinical-teal/10 flex items-center justify-center">
          <Plug2 className="h-4 w-4 text-clinical-cyan" />
        </div>
        <div>
          <h2 className="text-base font-display font-semibold">Integrations</h2>
          <p className="text-xs text-muted-foreground">Connect external services</p>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="rounded-xl border border-border/50 bg-surface/50 p-8 text-center">
          <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading integrations...</p>
        </div>
      )}

      {/* Integration Cards */}
      {!loading && (
        <div className="space-y-4">
          {/* Open Evidence Card - Behind Feature Flag */}
          {isFeatureEnabled('OPEN_EVIDENCE_INTEGRATION') && (
          <div className={cn(
            'rounded-xl border overflow-hidden transition-all duration-300',
            isConnected
              ? 'border-clinical-sage/30 bg-clinical-sage/5'
              : isExpired
                ? 'border-clinical-amber/30 bg-clinical-amber/5'
                : 'border-border/50 bg-surface/50 hover:border-primary/30'
          )}>
            {/* Card Header */}
            <div className="p-4 flex items-start gap-4">
              {/* Service Icon */}
              <div className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 relative',
                isConnected
                  ? 'bg-clinical-sage/20'
                  : 'bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20'
              )}>
                <BookOpen className={cn(
                  'h-6 w-6',
                  isConnected ? 'text-clinical-sage' : 'text-primary'
                )} />

                {/* Connection Status Indicator */}
                {isConnected && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-clinical-sage flex items-center justify-center">
                    <CheckCircle2 className="h-3 w-3 text-white" />
                  </span>
                )}
                {isExpired && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-clinical-amber flex items-center justify-center">
                    <Clock className="h-3 w-3 text-white" />
                  </span>
                )}
              </div>

              {/* Service Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-semibold">Open Evidence</h3>
                  {isConnected && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-clinical-sage/20 text-clinical-sage text-[10px] font-medium">
                      <span className="w-1 h-1 rounded-full bg-current animate-pulse" />
                      Connected
                    </span>
                  )}
                  {isExpired && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-clinical-amber/20 text-clinical-amber text-[10px] font-medium">
                      <AlertCircle className="h-3 w-3" />
                      Expired
                    </span>
                  )}
                </div>

                <p className="text-xs text-muted-foreground mb-3">
                  Search evidence-based medical literature with AI-synthesized responses and citations from NEJM, JAMA, Lancet, and clinical guidelines.
                </p>

                {/* Connected State Details */}
                {isConnected && oeIntegration && (
                  <div className="flex flex-wrap items-center gap-3 text-[11px]">
                    {oeIntegration.external_account_hint && (
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <span className="w-1 h-1 rounded-full bg-current" />
                        {oeIntegration.external_account_hint}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatRelativeTime(oeIntegration.expires_at)}
                    </span>
                    {oeIntegration.last_used_at && (
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <Sparkles className="h-3 w-3" />
                        Last used {new Date(oeIntegration.last_used_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}

                {/* Expired State */}
                {isExpired && (
                  <p className="text-xs text-clinical-amber">
                    Your session has expired. Please reconnect to continue using Open Evidence.
                  </p>
                )}
              </div>

              {/* Action Button */}
              <div className="flex-shrink-0">
                {isConnected ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="text-xs border-destructive/30 text-destructive hover:bg-destructive/10 hover:border-destructive/50"
                  >
                    {disconnecting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    ) : (
                      <Unplug className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Disconnect
                  </Button>
                ) : isExpired ? (
                  <Button
                    size="sm"
                    onClick={() => setConnectDialogOpen(true)}
                    className="text-xs bg-clinical-amber hover:bg-clinical-amber/90"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    Reconnect
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => setConnectDialogOpen(true)}
                    className="text-xs"
                  >
                    <Plug2 className="h-3.5 w-3.5 mr-1.5" />
                    Connect
                  </Button>
                )}
              </div>
            </div>

            {/* Connected State - Security Info */}
            {isConnected && (
              <div className="px-4 pb-4 pt-0">
                <div className="rounded-lg bg-background/50 border border-border/30 p-3">
                  <div className="flex items-start gap-2">
                    <Lock className="h-4 w-4 text-clinical-teal flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[11px] font-medium text-foreground">Encrypted Session</p>
                      <p className="text-[10px] text-muted-foreground">
                        Your session is stored with AES-256-GCM encryption. Your password is never stored.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Not Connected - Features List */}
            {!isConnected && !isExpired && (
              <div className="px-4 pb-4 pt-0">
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5 text-clinical-sage" />
                    <span>Unlimited searches</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5 text-clinical-sage" />
                    <span>NPI-verified access</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5 text-clinical-sage" />
                    <span>Clinical citations</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5 text-clinical-sage" />
                    <span>Secure & encrypted</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Coming Soon - Additional Integrations */}
          <div className="rounded-xl border border-dashed border-border/50 bg-surface/30 p-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-muted/50 mx-auto mb-3 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-muted-foreground/50" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">More integrations coming soon</p>
            <p className="text-xs text-muted-foreground/60 mt-1">
              Additional clinical resources and data sources
            </p>
          </div>
        </div>
      )}

      {/* Connect Dialog */}
      <OpenEvidenceConnectDialog
        open={connectDialogOpen}
        onOpenChange={setConnectDialogOpen}
        onSuccess={handleConnectSuccess}
      />
    </div>
  )
}


